import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';

import { useAuth } from '../../hooks/use-auth';
import { E2rRequest } from '../../entities/e2r-request';
import * as easyToReadService from '../../services/api/easy-2-read';
import AlertPrimary from '../../components/common/alerts/alert-primary';
import CommonButtonDefault from '../../components/common/buttons/default';
import CommonAnimatedSpinner from '../../components/common/animated/spinner';
import Heading1 from '../../components/common/headings/heading1';
import StyledInput from '../../components/common/form/styled-input';
import StyledTextarea from '../../components/common/form/styled-textarea';
import { strings } from '../../localization/strings';

const getRequestTitle = (request: E2rRequest): string => {
  if (request.converted_title) {
    return request.converted_title;
  }

  return request.original_title?.trim() || `Request #${request.id}`;
};

const getStatusClasses = (status: string): string => {
  if (status === 'completed') {
    return 'bg-green-100 text-green-800';
  }

  if (status === 'failed') {
    return 'bg-red-100 text-red-700';
  }

  return 'bg-amber-100 text-amber-800';
};

const getCreatorName = (request?: E2rRequest): string => {
  const firstName = request?.creator?.first_name?.trim() || request?.creator?.firstName?.trim() || '';
  const lastName = request?.creator?.last_name?.trim() || request?.creator?.lastName?.trim() || '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName !== '') {
    return fullName;
  }

  if (request?.creator?.name?.trim()) {
    return request.creator.name.trim();
  }

  return strings.easyToRead.unknownCreator;
};

function EasyToReadGeneratorPageComponent() {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(
    parseInt(searchParams.get('id') ?? '0', 10) ?? null,
  );
  const [originalTitle, setOriginalTitle] = useState<string>('');
  const [originalBody, setOriginalBody] = useState<string>('');
  const [feedbackMessage, setFeedbackMessage] = useState<string>('');
  const [downloadError, setDownloadError] = useState<string>('');
  const [isDownloadingWord, setIsDownloadingWord] = useState<boolean>(false);

  const {
    data: requests,
    error: requestsError,
    isLoading: isRequestsLoading,
  } = useQuery<E2rRequest[], Error>({
    queryKey: ['easy-to-read-requests'],
    queryFn: () => easyToReadService.list(token),
    refetchOnWindowFocus: false,
  });

  const {
    data: selectedRequest,
    error: selectedRequestError,
    isFetching: isSelectedRequestFetching,
  } = useQuery<E2rRequest, Error>({
    queryKey: ['easy-to-read-request', selectedRequestId],
    queryFn: () => easyToReadService.one(token, selectedRequestId as number),
    enabled: selectedRequestId !== null && selectedRequestId > 0,
    refetchOnWindowFocus: false,
    refetchInterval: (requestData) => {
      if (!requestData) {
        return false;
      }

      return requestData.status === 'queued' ? 3000 : false;
    },
  });

  const activeRequest = selectedRequestId === null ? undefined : selectedRequest;

  useEffect(() => {
    if (selectedRequest) {
      setOriginalTitle(selectedRequest.original_title);
      setOriginalBody(selectedRequest.original_body);
    }
  }, [selectedRequest]);

  const onSelectRequest = (id: number | null) => {
    setSelectedRequestId(id);
    setSearchParams(new URLSearchParams({ id: `${id}` }));
  };

  const createMutation = useMutation({
    mutationFn: ({ title, body }: { title: string; body: string }) => easyToReadService.create(token, title, body),
    onSuccess: async (data) => {
      setFeedbackMessage(strings.easyToRead.queuedMessage);
      onSelectRequest(data.requestId);
      await queryClient.invalidateQueries({ queryKey: ['easy-to-read-requests'] });
      await queryClient.invalidateQueries({ queryKey: ['easy-to-read-request', data.requestId] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => easyToReadService.destroy(token, id),
    onSuccess: async (_, deletedId) => {
      const remainingRequests = (requests || []).filter((item) => item.id !== deletedId);

      setFeedbackMessage(strings.easyToRead.deletedMessage);

      if (selectedRequestId === deletedId) {
        onSelectRequest(null);

        if (remainingRequests.length === 0) {
          setOriginalTitle('');
          setOriginalBody('');
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['easy-to-read-requests'] });
    },
  });

  const retryMutation = useMutation({
    mutationFn: (id: number) => easyToReadService.retry(token, id),
    onSuccess: async (data) => {
      setFeedbackMessage(strings.easyToRead.queuedMessage);
      onSelectRequest(data.requestId);
      await queryClient.invalidateQueries({ queryKey: ['easy-to-read-requests'] });
      await queryClient.invalidateQueries({ queryKey: ['easy-to-read-request', data.requestId] });
    },
  });

  const currentError =
    requestsError?.message ||
    selectedRequestError?.message ||
    (createMutation.error as Error | null)?.message ||
    (deleteMutation.error as Error | null)?.message ||
    (retryMutation.error as Error | null)?.message;

  const displayedError = currentError || downloadError;

  const canSubmit = originalTitle.trim().length >= 3 && originalBody.trim().length >= 10 && !createMutation.isLoading;

  const previewHtml = useMemo(() => {
    if (!activeRequest?.converted_body_html) {
      return '';
    }

    return activeRequest.converted_body_html;
  }, [activeRequest]);

  const onCreateNew = () => {
    onSelectRequest(null);
    setOriginalTitle('');
    setOriginalBody('');
    setFeedbackMessage('');
  };

  const onSubmit = async () => {
    setFeedbackMessage('');
    await createMutation.mutateAsync({
      title: originalTitle,
      body: originalBody,
    });
  };

  const downloadGeneratedText = async () => {
    if (!activeRequest) {
      return;
    }

    try {
      setDownloadError('');
      setIsDownloadingWord(true);
      const blob = await easyToReadService.downloadWord(token, activeRequest.id);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = `e2r-${activeRequest.id}.docx`;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (e) {
      setDownloadError((e as Error).message);
    } finally {
      setIsDownloadingWord(false);
    }
  };

  return (
    <div>
      <header className="mb-4 flex items-center justify-between gap-4">
        <div>
          <Heading1>{strings.navigation.easyToRead}</Heading1>
          <p className="mt-2 text-sm text-gray-600">{strings.easyToRead.description}</p>
        </div>
        <CommonButtonDefault type="button" secondary onClick={onCreateNew}>
          {strings.easyToRead.newRequest}
        </CommonButtonDefault>
      </header>

      {displayedError && (
        <div className="mb-4">
          <AlertPrimary type="danger" text={displayedError} />
        </div>
      )}

      {feedbackMessage && (
        <div className="mb-4">
          <AlertPrimary type="success" text={feedbackMessage} />
        </div>
      )}

      <main className="grid gap-4 xl:grid-cols-[280px,minmax(0,1.2fr),minmax(0,1fr)]">
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <div className="text-sm font-semibold text-gray-900">{strings.easyToRead.requests}</div>
            <div className="mt-1 text-xs text-gray-500">{strings.easyToRead.requestsHint}</div>
          </div>

          <div className="max-h-[70vh] overflow-y-auto">
            {isRequestsLoading && (
              <div className="flex justify-center px-4 py-8">
                <CommonAnimatedSpinner />
              </div>
            )}

            {!isRequestsLoading && (!requests || requests.length === 0) && (
              <div className="px-4 py-8 text-sm text-gray-500">{strings.easyToRead.noRequests}</div>
            )}

            {requests?.map((request) => (
              <button
                key={request.id}
                type="button"
                onClick={() => onSelectRequest(request.id)}
                className={`block w-full border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-50 ${
                  selectedRequestId === request.id ? 'bg-indigo-50' : 'bg-white'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium text-gray-900">{getRequestTitle(request)}</div>
                    <div className="mt-1 text-xs text-gray-500">
                      {strings.easyToRead.createdBy}: {getCreatorName(request)}
                    </div>
                    <div className="mt-1 text-xs text-gray-500">
                      {new Date(request.created_at).toLocaleString('lt-LT')}
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    {request.status === 'failed' && (
                      <button
                        type="button"
                        aria-label={strings.easyToRead.retry}
                        title={strings.easyToRead.retry}
                        className="rounded-md p-1 text-gray-400 hover:bg-white hover:text-indigo-600"
                        onClick={(event) => {
                          event.stopPropagation();
                          retryMutation.mutate(request.id);
                        }}
                        disabled={retryMutation.isLoading}
                      >
                        <ArrowPathIcon className={`h-4 w-4 ${retryMutation.isLoading ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                    <span
                      className={`rounded-full px-2 py-1 text-[11px] font-medium ${getStatusClasses(request.status)}`}
                    >
                      {request.status}
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <div className="text-sm font-semibold text-gray-900">{strings.easyToRead.sourceText}</div>
            <div className="mt-1 text-xs text-gray-500">{strings.easyToRead.sourceHint}</div>
          </div>

          <div className="space-y-4 p-4">
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {strings.easyToRead.sourceTitle}
              </div>
              <StyledInput
                value={originalTitle}
                onChange={(event) => setOriginalTitle(event.target.value)}
                placeholder={strings.easyToRead.titlePlaceholder}
              />
            </div>

            <StyledTextarea
              fullWidth
              rows={24}
              value={originalBody}
              onChange={(event) => setOriginalBody(event.target.value)}
              placeholder={strings.easyToRead.placeholder}
            />

            <div className="flex items-center justify-between gap-3">
              <div className="text-xs text-gray-500">{strings.easyToRead.minimumLength}</div>
              <CommonButtonDefault
                type="button"
                primary
                onClick={onSubmit}
                disabled={!canSubmit}
                isLoading={createMutation.isLoading}
              >
                {strings.easyToRead.convert}
              </CommonButtonDefault>
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-gray-200 bg-white">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">{strings.easyToRead.preview}</div>
              <div className="mt-1 text-xs text-gray-500">{strings.easyToRead.previewHint}</div>
            </div>
            <div className="flex">
              {activeRequest?.status === 'completed' && (
                <CommonButtonDefault
                  type="button"
                  className="mr-3"
                  secondary
                  sm
                  onClick={downloadGeneratedText}
                  disabled={isDownloadingWord}
                >
                  {strings.easyToRead.downloadWord}
                </CommonButtonDefault>
              )}
              {activeRequest && (
                <CommonButtonDefault
                  type="button"
                  danger
                  sm
                  onClick={() => deleteMutation.mutate(activeRequest.id)}
                  disabled={deleteMutation.isLoading}
                >
                  <span className="flex items-center gap-1">
                    <TrashIcon className="h-4 w-4" />
                    {strings.button.delete}
                  </span>
                </CommonButtonDefault>
              )}
            </div>
          </div>

          <div className="space-y-4 p-4">
            {isSelectedRequestFetching && (
              <div className="flex justify-center py-4">
                <CommonAnimatedSpinner />
              </div>
            )}

            {!activeRequest && <div className="text-sm text-gray-500">{strings.easyToRead.noSelection}</div>}

            {activeRequest && (
              <div className="space-y-1 text-xs text-gray-500">
                <div>
                  {strings.easyToRead.createdBy}: {getCreatorName(activeRequest)}
                </div>
                <div>
                  {strings.easyToRead.createdAt}: {new Date(activeRequest.created_at).toLocaleString('lt-LT')}
                </div>
              </div>
            )}

            {activeRequest?.status === 'queued' && (
              <AlertPrimary
                type="warning"
                title={strings.easyToRead.waitingTitle}
                text={strings.easyToRead.waitingText}
              />
            )}

            {activeRequest?.status === 'failed' && activeRequest.error && (
              <AlertPrimary type="danger" title={strings.easyToRead.failedTitle} text={activeRequest.error} />
            )}

            {activeRequest?.status === 'completed' && (
              <>
                <h2 className="mt-2 text-2xl font-semibold text-gray-900">
                  {activeRequest.converted_title || getRequestTitle(activeRequest)}
                </h2>
                {/* eslint-disable react/no-danger */}
                {previewHtml ? (
                  <div
                    className="prose prose-sm max-w-none text-gray-900"
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                  />
                ) : (
                  <div className="text-sm text-gray-500">{strings.easyToRead.noPreview}</div>
                )}
                {/* eslint-enable react/no-danger */}
              </>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

export default EasyToReadGeneratorPageComponent;
